import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  PhotoIcon,
  MapPinIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';

const WorkOrderPublicUpdate = ({ updateToken }) => {
  const [workOrder, setWorkOrder] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [plotGeometry, setPlotGeometry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWorkOrder();
  }, [updateToken]);

  const fetchWorkOrder = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/work-orders/public/${updateToken}`);
      if (!response.ok) {
        throw new Error('Work order not found or link expired');
      }
      const data = await response.json();
      setWorkOrder(data.workOrder);
      setStatuses(data.statuses);
      setPlotGeometry(data.plotGeometry);
      setSelectedStatus(data.workOrder.status?.id || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewAttachment = async (att) => {
    try {
      const response = await fetch(`/api/work-orders/public/${updateToken}/attachments/${att.id}`);
      if (response.ok) {
        const data = await response.json();
        if (data.fileData) {
          const win = window.open('', '_blank');
          if (att.attachmentType === 'image') {
            win.document.write(`<html><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#111"><img src="${data.fileData}" style="max-width:100%;max-height:100vh;object-fit:contain" /></body></html>`);
          } else {
            const link = document.createElement('a');
            link.href = data.fileData;
            link.download = att.fileName || att.title || 'attachment';
            link.click();
            win.close();
          }
        }
      }
    } catch (error) { console.error('Error viewing attachment:', error); }
  };

  const handleSubmit = async () => {
    const statusChanged = selectedStatus && parseInt(selectedStatus) !== workOrder.status?.id;
    if (!statusChanged && !note.trim()) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const body = {};
      if (statusChanged) body.statusId = parseInt(selectedStatus);
      if (note.trim()) body.note = note.trim();

      const response = await fetch(`/api/work-orders/public/status/${updateToken}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update');
      }

      setSuccess(true);
      setNote('');
      fetchWorkOrder();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin h-10 w-10 border-2 border-brand-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error && !workOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!workOrder) return null;

  // Check if work order is completed (status is final)
  const currentStatus = statuses.find(s => s.id === workOrder.status?.id);
  const isCompleted = currentStatus?.isFinal || false;

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Work Order Completed</h1>
            <p className="text-gray-600 mb-4">"{workOrder.title}" has been marked as {workOrder.status?.name || 'completed'}.</p>
            {workOrder.completedDate && (
              <p className="text-sm text-gray-500">
                Completed on {new Date(workOrder.completedDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const typeLabels = {
    funeral_preparation: 'Funeral Preparation',
    headstone_installation: 'Headstone Installation',
    repair: 'Repair', maintenance: 'Maintenance',
    landscaping: 'Landscaping', other: 'Other'
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: workOrder.status?.color || '#6b7280' }}
            />
            <span className="text-sm font-medium" style={{ color: workOrder.status?.color || '#6b7280' }}>
              {workOrder.status?.name || 'Unknown'}
            </span>
          </div>
          <h1 className="text-xl font-bold text-gray-900">{workOrder.title}</h1>
          {workOrder.description && (
            <p className="mt-2 text-gray-600 text-sm">{workOrder.description}</p>
          )}
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
            <span className="bg-gray-100 px-2 py-1 rounded">{typeLabels[workOrder.workOrderType]}</span>
            <span className="bg-gray-100 px-2 py-1 rounded capitalize">Priority: {workOrder.priority}</span>
            {workOrder.dueDate && (
              <span className="bg-gray-100 px-2 py-1 rounded">Due: {new Date(workOrder.dueDate).toLocaleDateString()}</span>
            )}
          </div>
        </div>

        {/* Plot Map */}
        {plotGeometry && workOrder.includeMap && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <h2 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <MapPinIcon className="h-4 w-4" />
              Plot Location
            </h2>
            <div className="bg-gray-100 rounded-md p-3">
              <p className="text-sm text-gray-700">
                <strong>{plotGeometry.alias || 'Plot'}</strong>
                {plotGeometry.section && ` - Section: ${plotGeometry.section}`}
              </p>
              <p className="text-xs text-gray-500 mt-1">Map view available in full application</p>
            </div>
          </div>
        )}

        {/* Attachments */}
        {workOrder.attachments?.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <h2 className="font-medium text-gray-900 mb-2">Attachments</h2>
            <div className="grid grid-cols-2 gap-2">
              {workOrder.attachments.map(att => (
                <div key={att.id} onClick={() => handleViewAttachment(att)} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100">
                  <PhotoIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-xs truncate">{att.title || att.fileName}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {workOrder.notes?.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-4 mb-4">
            <h2 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
              <ChatBubbleLeftIcon className="h-4 w-4" />
              Notes
            </h2>
            <div className="space-y-2">
              {workOrder.notes.map(n => (
                <div key={n.id} className="bg-gray-50 rounded-md p-2">
                  <p className="text-sm">{n.note}</p>
                  <p className="text-xs text-gray-400 mt-1">{n.createdByName} - {new Date(n.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Update */}
        <div className="bg-white rounded-lg shadow-lg p-4">
          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
              <span className="text-green-800 text-sm">Saved successfully!</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <h2 className="font-medium text-gray-900 mb-3">Update Status</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              {statuses.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedStatus(s.id)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    selectedStatus === s.id
                      ? 'border-brand-green-500 bg-brand-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="inline-block w-2.5 h-2.5 rounded-full mr-2" style={{ backgroundColor: s.color }} />
                  {s.name}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-1">Add a note (optional)</label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={2}
                placeholder="Any comments about this update..."
                className="w-full px-3 py-2 border rounded-md text-sm"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || (!note.trim() && !(selectedStatus && parseInt(selectedStatus) !== workOrder.status?.id))}
              className="w-full py-3 bg-brand-green-600 text-white rounded-lg font-medium hover:bg-brand-green-700 disabled:bg-gray-300 text-sm"
            >
              {isSubmitting ? 'Saving...' : (selectedStatus && parseInt(selectedStatus) !== workOrder.status?.id ? 'Update Status' : 'Add Note')}
            </button>
          </div>
        </div>

        {/* Status History */}
        {workOrder.statusHistory?.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-4 mt-4">
            <h2 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <ClockIcon className="h-4 w-4" />
              History
            </h2>
            <div className="space-y-2">
              {workOrder.statusHistory.map((entry, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                  <span className="font-medium">{entry.statusName}</span>
                  <span className="text-gray-400">- {entry.changedBy}</span>
                  <span className="text-gray-400 ml-auto text-xs">{new Date(entry.date).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkOrderPublicUpdate;
